import { observer } from "mobx-react-lite";
import { useState } from "react";
import styled from "styled-components";

import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import { Pill } from "../ui/controls";
import { Icon } from "../ui/Icon";
import { SearchBox } from "../ui/SearchBox";
import { SlideOver } from "../ui/SlideOver";
import { MenuLink, MenuList, MenuRow } from "./menuStyles";

/** Slides over the sheet with all songs. A song with more than one version folds open to show them. */
export const SongTreeMenu = observer(function SongTreeMenu() {
    const { library, navigation, viewer } = useStores();
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState<Set<string>>(() => new Set(viewer.song ? [viewer.song.id] : []));
    const show = (chordSheetId: string) => navigation.replace({ name: "sheet", chordSheetId, from: { kind: "songs" } });
    const footer = <MenuLink onClick={() => navigation.go({ name: "setlists" })}>Setlists</MenuLink>;

    function toggle(songId: string) {
        const next = new Set(open);

        if (next.has(songId)) next.delete(songId);
        else next.add(songId);

        setOpen(next);
    }

    return (
        <SlideOver label="all songs" title="All songs" footer={footer} onClose={() => viewer.toggle("menu")}>
            <Search label="Search songs" placeholder="Search" value={query} onChange={setQuery} />
            <MenuList>
                {library.findSongs(query).map((song) => {
                    const versions = library.versionsOf(song.id);
                    const isOpen = open.has(song.id);

                    if (versions.length === 1) {
                        return (
                            <li key={song.id}>
                                <MenuRow $indent aria-current={versions[0].id === viewer.chordSheetId} onClick={() => show(versions[0].id)}>
                                    <Title>{song.title}</Title>
                                </MenuRow>
                            </li>
                        );
                    }

                    return (
                        <li key={song.id}>
                            <MenuRow aria-expanded={isOpen} onClick={() => toggle(song.id)}>
                                <Chevron $open={isOpen}>
                                    <Icon name="forward" size={16} />
                                </Chevron>
                                <Title>{song.title}</Title>
                                <Pill>{versions.length}</Pill>
                            </MenuRow>
                            {isOpen && (
                                <MenuList>
                                    {versions.map((sheet) => (
                                        <li key={sheet.id}>
                                            <MenuRow $indent aria-current={sheet.id === viewer.chordSheetId} onClick={() => show(sheet.id)}>
                                                {sheet.name || song.title}
                                            </MenuRow>
                                        </li>
                                    ))}
                                </MenuList>
                            )}
                        </li>
                    );
                })}
            </MenuList>
        </SlideOver>
    );
});

const Search = styled(SearchBox)`
    display: block;
    padding: 0 2px 10px;
`;

const Title = styled.span`
    flex: 1;
    font-weight: 600;
`;

const Chevron = styled.span<{ $open: boolean }>`
    display: inline-flex;
    color: ${color.textMuted};
    transition: transform 0.15s;
    transform: rotate(${({ $open }) => ($open ? 90 : 0)}deg);
`;
